'use client';
import React, { useState, useEffect } from 'react';
import AppLogo from '@/components/ui/AppLogo';
import Link from 'next/link';
import { WeatherData } from '@/lib/types';

interface AppHeaderProps {
  weatherData: WeatherData;
  onRefresh: () => void;
  isRefreshing: boolean;
}

export default function AppHeader({ weatherData, onRefresh, isRefreshing }: AppHeaderProps) {
  const [currentLocalTime, setCurrentLocalTime] = useState('');
  const [currentZuluTime, setCurrentZuluTime] = useState('');

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const localStr = now.toLocaleTimeString('fr-CA', {
        timeZone: 'America/Toronto',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      const zuluHH = now.getUTCHours().toString().padStart(2, '0');
      const zuluMM = now.getUTCMinutes().toString().padStart(2, '0');
      setCurrentLocalTime(localStr);
      setCurrentZuluTime(`${zuluHH}:${zuluMM}Z`);
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const sourceColor =
    weatherData.dataSource === 'CYRC' ? 'text-green-700 bg-green-50 border-green-200'
      : weatherData.dataSource === 'CYBG'? 'text-amber-700 bg-amber-50 border-amber-200' :'text-slate-500 bg-slate-50 border-slate-200';

  return (
    <header
      className="bg-white border-b border-slate-200 shrink-0"
      style={{ boxShadow: '0 1px 4px rgba(30,58,95,0.07)', paddingTop: 'env(safe-area-inset-top)' }}
    >
      {/* ── DESKTOP layout (md+): single row ── */}
      <div className="hidden md:flex items-center justify-between gap-4 px-4 py-2">
        {/* Left: Logo + Title */}
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex items-center gap-2.5 shrink-0">
            <AppLogo size={32} />
            <div className="w-px h-8 bg-slate-200" />
          </div>
          <div className="min-w-0">
            <h1 className="text-base font-black text-slate-800 leading-tight truncate">
              MétéoWeather APSL – CYRC
            </h1>
            <p className="text-xs text-slate-400 leading-tight">
              Chicoutimi / Saint-Honoré
            </p>
            <p className="text-slate-400 leading-tight mt-0.5" style={{ fontSize: '0.6rem' }}>
              Vous souhaitez cette application pour votre club ou association ?{' '}
              <Link
                href="/contact-gestion-pvjm"
                className="text-slate-500 underline underline-offset-2 hover:text-slate-600 transition-colors"
              >
                Cliquez ici
              </Link>
            </p>
          </div>
        </div>

        {/* Right: Clock + Refresh + Last update + Source */}
        <div className="flex items-center gap-2 shrink-0">
          {/* Clock */}
          <div className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-1.5 border border-slate-200">
            <svg className="w-4 h-4 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" strokeWidth="2" />
              <path strokeLinecap="round" d="M12 6v6l4 2" strokeWidth="2" />
            </svg>
            <div className="flex items-center gap-3">
              <div className="flex flex-col items-center leading-none">
                <span className="font-tabular text-xl font-black text-slate-800 leading-none">{currentLocalTime}</span>
                <span className="text-xs text-slate-400 font-semibold tracking-wide uppercase" style={{ fontSize: '0.6rem' }}>HEURE LOCALE</span>
              </div>
              <div className="w-px h-8 bg-slate-200" />
              <div className="flex flex-col items-center leading-none">
                <span className="font-tabular text-xl font-black text-blue-600 leading-none">{currentZuluTime}</span>
                <span className="text-xs text-blue-400 font-semibold tracking-wide uppercase" style={{ fontSize: '0.6rem' }}>ZOULOU</span>
              </div>
            </div>
          </div>

          {/* Refresh */}
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="w-8 h-8 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center hover:bg-blue-50 hover:border-blue-200 transition-all duration-150 active:scale-95 disabled:opacity-50"
            title="Actualiser"
          >
            <svg className={`w-4 h-4 text-blue-500 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>

          {/* Last update */}
          <div className="flex flex-col items-end border-l border-slate-200 pl-3">
            <span className="font-bold text-slate-400 uppercase tracking-wide leading-none" style={{ fontSize: '0.6rem' }}>DERNIÈRE MISE À JOUR</span>
            <span className="text-sm font-black text-slate-800 leading-tight font-tabular">
              {weatherData.lastUpdated}{' '}
              <span className="text-xs text-slate-400 font-normal">(il y a {weatherData.metar.ageMinutes} min)</span>
            </span>
          </div>

          {/* Source badge */}
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border font-black text-sm ${sourceColor}`}>
            <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
            </svg>
            <div className="flex flex-col items-start leading-none">
              <span className="text-xs font-bold opacity-70 uppercase tracking-wide" style={{ fontSize: '0.6rem' }}>SOURCE</span>
              <span className="text-base font-black leading-none">{weatherData.dataSource}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── MOBILE layout (< md): 3-line stacked ── */}
      <div className="flex md:hidden flex-col px-3 py-2 gap-1.5">
        {/* Line 1: Logo + Title + Refresh */}
        <div className="flex items-center gap-2">
          <AppLogo size={26} />
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-black text-slate-800 leading-tight truncate">
              MétéoWeather APSL – CYRC
            </h1>
            <p className="text-xs text-slate-400 leading-none" style={{ fontSize: '0.6rem' }}>
              Chicoutimi / Saint-Honoré
            </p>
            <p className="text-slate-400 leading-tight" style={{ fontSize: '0.55rem' }}>
              Application pour votre club ?{' '}
              <Link
                href="/contact-gestion-pvjm"
                className="text-slate-500 underline underline-offset-2 hover:text-slate-600 transition-colors"
              >
                Cliquez ici
              </Link>
            </p>
          </div>
          {/* Source badge compact */}
          <div className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-xs font-black ${sourceColor}`}>
            <span>{weatherData.dataSource}</span>
          </div>
          {/* Refresh */}
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="w-7 h-7 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center active:scale-95 disabled:opacity-50"
            title="Actualiser"
          >
            <svg className={`w-3.5 h-3.5 text-blue-500 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>

        {/* Line 2: Heure locale / Zoulou */}
        <div className="flex items-center gap-3 bg-slate-50 rounded-lg px-2.5 py-1 border border-slate-200">
          <svg className="w-3.5 h-3.5 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" strokeWidth="2" />
            <path strokeLinecap="round" d="M12 6v6l4 2" strokeWidth="2" />
          </svg>
          <div className="flex items-center gap-1 leading-none">
            <span className="font-tabular text-sm font-black text-slate-800">{currentLocalTime}</span>
            <span className="text-slate-400 font-semibold" style={{ fontSize: '0.6rem' }}>LOC</span>
          </div>
          <div className="w-px h-4 bg-slate-200" />
          <div className="flex items-center gap-1 leading-none">
            <span className="font-tabular text-sm font-black text-blue-600">{currentZuluTime}</span>
            <span className="text-blue-400 font-semibold" style={{ fontSize: '0.6rem' }}>Z</span>
          </div>
        </div>

        {/* Line 3: Dernière mise à jour */}
        <div className="flex items-center gap-1.5">
          <span className="font-bold text-slate-400 uppercase tracking-wide" style={{ fontSize: '0.55rem' }}>MÀJ :</span>
          <span className="text-xs font-black text-slate-700 font-tabular">{weatherData.lastUpdated}</span>
          <span className="text-slate-400" style={{ fontSize: '0.6rem' }}>(il y a {weatherData.metar.ageMinutes} min)</span>
        </div>
      </div>
    </header>
  );
}